/**
 * Derivacoes CLIENT-SIDE da aba RESUMO (1 linha por empresa) e das headlines.
 *
 * O `/resumo` do BFF ja' entrega situacao/posicao/bots por empresa; aqui fica so' o
 * que e' regra de EXIBICAO: trilhos paralelos da coluna Bots, ordenacao por coluna
 * (incluindo Grupo — no-op no prototipo, corrigido aqui), filtros combinaveis e as
 * headlines calculadas. Nada de literal: toda contagem sai dos MESMOS dados filtrados
 * que a tabela mostra, senao headline e tabela divergem no primeiro filtro.
 */

import type {
  IBotDaEmpresa,
  IEmpresaResumo,
  IContagens,
  IResponsaveisPorEmpresa,
  IVinculoBot,
  SituacaoEmpresa,
  StatusEsteira,
} from '../api';
import { montarLinhasBots, nomeEmpresa } from './derivarProcessos';
import type { IEmpresaComStatus } from './derivarProcessos';
import { casaBuscaPorModo, ModoBusca } from './codigoEmpresa';
import { empresaBateRegime, regimeProprio } from './regimeEfetivo';

/** KPIs de situacao, na ordem do prototipo, com o tom visual de cada um. */
export const SITUACOES_KPI: Array<{ situacao: SituacaoEmpresa; label: string; cor: string }> = [
  { situacao: 'Concluída', label: 'Concluídas', cor: 'green' },
  { situacao: 'Em falha', label: 'Em falha', cor: 'red' },
  { situacao: 'Em andamento', label: 'Em andamento', cor: 'blue' },
  { situacao: 'Sem registros', label: 'Sem registros', cor: 'gray' },
];

const plural = (n: number, singular: string, plurals: string): string =>
  `${n} ${n === 1 ? singular : plurals}`;

/**
 * Empresa INATIVA ou FORA da janela de vigencia (pedido 08/09): o BFF parou de
 * excluir essas empresas do /resumo — vem tudo com flags e o front decide. Flags
 * ausentes (BFF antigo) = empresa dentro das regras, como sempre foi (retrocompat).
 */
export const empresaForaDasRegras = (e: IEmpresaResumo): boolean =>
  e.ativa === false || e.fora_vigencia === true;

/**
 * Exclusao GLOBAL default (regra de negocio 08/09): empresa inativa/fora da janela
 * de vigencia nao aparece em LUGAR NENHUM por default — aplicada NA FONTE
 * (index.tsx, sobre o /resumo cru), para Processos (select de empresa, board das
 * sentinelas, fila e popups) nunca ver essas empresas. A UNICA excecao e' o
 * filtro de SITUACAO CADASTRAL da aba Resumo (09/09), que consome o dataset CRU
 * da propria aba para poder mostrar "Inativas"/"Todas".
 */
export const empresasDentroDasRegras = <T extends IEmpresaResumo>(empresas: T[]): T[] =>
  empresas.filter(e => !empresaForaDasRegras(e));

// ── Situacao cadastral (filtro de 3 estados — pedido 09/09) ──────────────────

/**
 * Recorte por SITUACAO CADASTRAL da empresa na aba Resumo (pedido 09/09):
 * - `ativas` (default): so' empresa DENTRO das regras — o comportamento historico
 *   da exclusao global (ativa e dentro da janela de vigencia);
 * - `inativas`: SO' o complemento — inativa (`ativa === false`) OU fora da janela
 *   (`fora_vigencia === true`), as mesmas que `empresaForaDasRegras` marca;
 * - `todas`: sem recorte.
 *
 * Substitui o booleano `incluirForaDasRegras` (que so' sabia esconder/revelar e
 * nao conseguia isolar as inativas) e vira a UNICA forma de revelar inativas na
 * aba — o antigo "Ver todas as empresas" volta a significar so' o que o nome diz:
 * ignorar o recorte de PERIODO.
 */
export type SituacaoCadastral = 'ativas' | 'inativas' | 'todas';

/**
 * Opcoes do filtro, na ordem de exibicao (fonte unica do select e dos testes).
 *
 * Os rotulos NOMEIAM a dimensao (achado BAIXO B1 da revisao 09/09): os vizinhos da
 * fila usam placeholders do tipo "Todos os regimes"/"Todos os grupos", entao um
 * "Ativas"/"Todas" solto nao dizia de QUE se fala — e um "Todas" indistinguivel dos
 * placeholders vizinhos, logo neste select que HERDOU do botao "Ver todas as
 * empresas" a funcao de revelar inativas (a unica sinalizacao que o usuario tem).
 */
export const SITUACOES_CADASTRAIS: Array<{ value: SituacaoCadastral; label: string }> = [
  { value: 'ativas', label: 'Empresas ativas' },
  { value: 'inativas', label: 'Empresas inativas' },
  { value: 'todas', label: 'Ativas e inativas' },
];

/** A empresa passa no recorte de situacao cadastral escolhido? */
export const casaSituacaoCadastral = (e: IEmpresaResumo, sit: SituacaoCadastral): boolean => {
  if (sit === 'todas') return true;
  const fora = empresaForaDasRegras(e);
  return sit === 'inativas' ? fora : !fora;
};

/** Recorte de uma lista pela situacao cadastral — preserva a ordem de chegada. */
export const filtrarPorSituacaoCadastral = <T extends IEmpresaResumo>(
  empresas: T[],
  sit: SituacaoCadastral,
): T[] => (sit === 'todas' ? empresas : empresas.filter(e => casaSituacaoCadastral(e, sit)));

/**
 * Rotulo de alerta sob o codigo (achado da revisao 08/09): "inativa" SO quando
 * `ativa === false`; empresa ATIVA porem fora da janela exibe "fora de vigencia"
 * (mesmo estilo visual). Inativa E fora da janela → "inativa" (estado mais forte).
 * Null = dentro das regras, nenhum rotulo.
 */
export const rotuloForaDasRegras = (e: IEmpresaResumo): string | null => {
  if (e.ativa === false) return 'inativa';
  if (e.fora_vigencia === true) return 'fora de vigência';
  return null;
};

/**
 * Bots concluidos da empresa = status `done` OU `warn` (warn NAO trava — regra do
 * rastreador, contrato §3.2). Derivado da lista `bots` para a regra valer mesmo se o
 * campo agregado do BFF divergir; sem lista, confia no agregado.
 */
export const botsConcluidosDaEmpresa = (e: IEmpresaResumo): number =>
  e.bots.length
    ? e.bots.filter(b => b.status === 'done' || b.status === 'warn').length
    : e.bots_concluidos;

/** Bot da rota da empresa anotado com a dependencia (para os trilhos paralelos). */
export interface IBotComDep extends IBotDaEmpresa {
  dep_bot: string | null;
}

// ── Recorte de regime por nivel NA FONTE (correcao da revisao 08/09) ─────────

/**
 * `IEmpresaResumo` apos o recorte de regime por nivel aplicado NA FONTE:
 * `botsOcultosPorRegime` (client-side, nunca vem do BFF) conta quantos bots da
 * rota o recorte escondeu — e' o que permite a celula do trilho dizer "oculto
 * pelo regime" em vez de ficar vazia sem explicacao.
 */
export interface IEmpresaResumoRecortada extends IEmpresaResumo {
  botsOcultosPorRegime?: number;
}

/**
 * Situacao derivada de uma rota de bots — mesma semantica do agregado do BFF:
 * algum `err` trava ("Em falha"); `run`/`wait` = "Em andamento"; so' done/warn =
 * "Concluída" (warn NAO trava, regra do rastreador); rota vazia = "Sem registros".
 */
export const situacaoDaRota = (bots: IBotDaEmpresa[]): SituacaoEmpresa => {
  if (!bots.length) return 'Sem registros';
  if (bots.some(b => b.status === 'err')) return 'Em falha';
  if (bots.some(b => b.status === 'run' || b.status === 'wait')) return 'Em andamento';
  return 'Concluída';
};

/**
 * Aplica o recorte de regime por nivel (regime PROPRIO do bot — contexto sem
 * grupo, o mesmo dos trilhos) NA FONTE do /resumo (correcao M1 da revisao 08/09):
 * antes, `trilhosDaEmpresa` filtrava so' a EXIBICAO do trilho, mas o casamento de
 * status/bot de `filtrarEmpresasResumo` e a situacao/KPI continuavam lendo a rota
 * COMPLETA — linha aparecia por status de bot que a tabela nao mostrava. Aqui a
 * linha inteira passa a derivar da MESMA rota filtrada que o trilho exibe: bot
 * oculto nao conta em filtro nenhum, `situacao`/`bots_total`/`bots_concluidos`
 * sao rederivados dos bots visiveis, e `posicao` apontando para bot oculto e'
 * limpa (bot oculto nao conta como etapa ativa).
 *
 * SEM regime configurado em bot nenhum, devolve a MESMA referencia recebida —
 * zero mudanca de caminho, `e.situacao` do servidor intacta. Empresa cuja rota o
 * recorte nao toca tambem mantem a MESMA referencia (e a situacao do servidor).
 */
export const aplicarRegimeProprioNoResumo = (
  empresas: IEmpresaResumo[],
  regimeProprioDoBot?: ReadonlyMap<string, string | null>,
): IEmpresaResumoRecortada[] => {
  let temRegimeConfigurado = false;
  regimeProprioDoBot?.forEach(regime => {
    if (regime) temRegimeConfigurado = true;
  });
  if (!temRegimeConfigurado) return empresas;
  return empresas.map(e => {
    const visiveis = e.bots.filter(b =>
      empresaBateRegime(e.empresa, regimeProprioDoBot?.get(b.bot) ?? null),
    );
    if (visiveis.length === e.bots.length) return e;
    const ocultos = e.bots.filter(b => !visiveis.includes(b));
    const posicaoOculta = e.posicao !== null && ocultos.some(b => b.bot === e.posicao?.bot);
    return {
      ...e,
      bots: visiveis,
      bots_total: visiveis.length,
      bots_concluidos: visiveis.filter(b => b.status === 'done' || b.status === 'warn').length,
      situacao: situacaoDaRota(visiveis),
      posicao: posicaoOculta ? null : e.posicao,
      botsOcultosPorRegime: ocultos.length,
    };
  });
};

/**
 * Composicao NA FONTE do dataset da pagina (extraida de index.tsx — fechamento do
 * MEDIO de pinagem do verificador 09/09): a LIGACAO exclusao-global → recorte de
 * regime nao era testavel enquanto vivia num useMemo — um revert parcial em
 * conflito de merge podia restaurar a incoerencia (bot oculto contando em
 * filtro/situacao/KPI) com a suite verde. Recebe o /resumo CRU + os vinculos e
 * devolve o dataset que TUDO na pagina consome: empresas dentro das regras
 * (inativa/fora de vigencia fora, `empresasDentroDasRegras`) e ja recortadas pelo
 * regime PROPRIO de cada bot (`aplicarRegimeProprioNoResumo` — contexto sem
 * grupo, nunca a heranca da cadeia). index.tsx so memoiza a chamada.
 */
export const derivarEmpresasDaPagina = (
  empresasCruas: IEmpresaResumo[],
  vinculos: IVinculoBot[],
): IEmpresaResumoRecortada[] =>
  aplicarRegimeProprioNoResumo(
    empresasDentroDasRegras(empresasCruas),
    new Map<string, string | null>(vinculos.map(v => [v.bot, regimeProprio(v)])),
  );

/**
 * MESMA composicao de `derivarEmpresasDaPagina` SEM a exclusao global (pedido
 * 09/09): so' o recorte de regime por nivel, preservando as empresas inativas/fora
 * da vigencia com as flags do BFF. E' a base do filtro de SITUACAO CADASTRAL da aba
 * Resumo — o unico consumidor autorizado a ver o dataset completo do PERIODO.
 *
 * Por que uma funcao propria e nao "aplicar regime e filtrar depois na aba": o
 * recorte de regime tem de acontecer ANTES de qualquer contagem (correcao M1) e
 * `derivarEmpresasDaPagina` ja' comeu as inativas — a aba nunca conseguiria
 * recupera-las sem repetir a ordem das etapas e arriscar diverge-las. As duas
 * funcoes compoem: `derivarEmpresasDaPagina(cru, v)` ===
 * `empresasDentroDasRegras(derivarEmpresasComRegime(cru, v))`.
 */
export const derivarEmpresasComRegime = (
  empresasCruas: IEmpresaResumo[],
  vinculos: IVinculoBot[],
): IEmpresaResumoRecortada[] =>
  aplicarRegimeProprioNoResumo(
    empresasCruas,
    new Map<string, string | null>(vinculos.map(v => [v.bot, regimeProprio(v)])),
  );

/**
 * Trilhos PARALELOS da coluna Bots — o `routeLines()` do prototipo: cada trilho e'
 * uma cadeia de dependencia direta (`dep_bot`) DENTRO da rota da empresa. Bot cuja
 * dependencia nao esta na rota vira raiz (senao o trilho ficaria preso a um bot
 * invisivel) — comportamento que `montarLinhasBots` ja' garante.
 *
 * REGIME POR NIVEL (08/09): `regimeProprioDoBot` oculta do trilho o bot cujo regime
 * PROPRIO esta definido e nao bate com o da empresa — o trilho do Resumo e' contexto
 * SEM grupo, entao vale so o regime proprio, nunca a heranca da cadeia. O filtro
 * acontece ANTES de encadear: dependente de bot ocultado vira raiz normalmente.
 * Desde a correcao M1 (revisao 08/09) o MESMO recorte ja' e' aplicado na FONTE
 * (`aplicarRegimeProprioNoResumo`, em index.tsx) — o filtro daqui e' idempotente
 * e fica como defesa para chamadas com dataset nao recortado.
 */
export const trilhosDaEmpresa = (
  e: IEmpresaResumo,
  depDoBot: ReadonlyMap<string, string | null>,
  regimeProprioDoBot?: ReadonlyMap<string, string | null>,
): IBotComDep[][] =>
  montarLinhasBots(
    e.bots
      .filter(b => empresaBateRegime(e.empresa, regimeProprioDoBot?.get(b.bot) ?? null))
      .map(b => ({ ...b, dep_bot: depDoBot.get(b.bot) ?? null })),
  );

// ── Ordenacao ────────────────────────────────────────────────────────────────

export type ColunaResumo = 'empresa' | 'grupo' | 'regime' | 'bots' | 'atividade';

export interface OrdenacaoResumo {
  coluna: ColunaResumo;
  asc: boolean;
}

/**
 * Chave de ordenacao por coluna. `grupo` ordena por `grupo_de_empresas` — a coluna
 * existia no prototipo mas a funcao de chave nao a tratava (seta aparecia e nada
 * acontecia; bug registrado na auditoria C2 §2.4, item 3 do quadro de divergencias).
 */
export const chaveDeOrdenacao = (e: IEmpresaResumo, coluna: ColunaResumo): string | number => {
  switch (coluna) {
    case 'grupo':
      return (e.empresa?.grupo_de_empresas ?? '').toLowerCase();
    case 'regime':
      return (e.empresa?.regime ?? '').toLowerCase();
    case 'bots': {
      // Razao concluidos/total — empresa sem rota vai para o comeco no ascendente.
      const total = e.bots.length || e.bots_total;
      return total ? botsConcluidosDaEmpresa(e) / total : -1;
    }
    case 'atividade':
      return e.ultima_atividade ?? '';
    default:
      return nomeEmpresa(e).toLowerCase();
  }
};

/** Ordena sem mutar. Chaves iguais preservam a ordem de chegada (sort estavel). */
export const ordenarEmpresasResumo = <T extends IEmpresaResumo>(
  empresas: T[],
  ordenacao: OrdenacaoResumo | null,
): T[] => {
  if (!ordenacao) return empresas;
  const { coluna, asc } = ordenacao;
  return empresas.slice().sort((a, b) => {
    const ka = chaveDeOrdenacao(a, coluna);
    const kb = chaveDeOrdenacao(b, coluna);
    if (ka < kb) return asc ? -1 : 1;
    if (ka > kb) return asc ? 1 : -1;
    return 0;
  });
};

// ── Filtros da tabela ────────────────────────────────────────────────────────

export interface FiltrosResumo {
  /** Toggle dos KPIs — null = sem recorte de situacao. */
  situacao: SituacaoEmpresa | null;
  busca: string;
  /**
   * Tipo de pesquisa do textbox (pedido 08/09): a busca casa SÓ no campo escolhido
   * — "razao" (razão social/nome exibido) ou "id" (código da empresa). Substitui o
   * nome-OU-código automático da Fase 12 na aba Resumo.
   */
  buscaModo: ModoBusca;
  regime: string | null;
  /** codi_emp. */
  empresa: string | null;
  /** tb_empresa.grupo_de_empresas (carteira) — nao confundir com grupo de BOT. */
  grupoEmpresa: string | null;
  bot: string | null;
  responsavel: string | null;
  area: string | null;
  /**
   * Multi-selecao de STATUS (pedido 04/09) — VAZIO = sem filtro. A linha aparece se
   * ALGUM bot da rota da empresa esta' num status selecionado.
   */
  status: StatusEsteira[];
  /**
   * SITUACAO CADASTRAL (pedido 09/09) — 3 estados: "ativas" (default, comportamento
   * historico: esconde `empresaForaDasRegras`), "inativas" (SO' as inativas/fora da
   * janela) e "todas". Substitui o booleano `incluirForaDasRegras`, que so' sabia
   * esconder ou revelar — nunca isolar.
   */
  situacaoCadastral: SituacaoCadastral;
}

export const FILTROS_RESUMO_VAZIOS: FiltrosResumo = {
  situacao: null,
  busca: '',
  buscaModo: 'razao',
  regime: null,
  empresa: null,
  grupoEmpresa: null,
  bot: null,
  responsavel: null,
  area: null,
  status: [],
  situacaoCadastral: 'ativas',
};

/**
 * Casamento dos filtros Responsável/Área sobre a UNIÃO das duas fontes (pedido
 * 08/09): `e.areas` do /resumo (cadastro, fonte primária das células) + o mapa
 * das execuções ativas (fallback) — assim o filtro também acha as empresas que
 * só têm dado do cadastro (sem execução ativa), e nada que casava antes deixa
 * de casar. Sem dado em NENHUMA fonte, filtro ativo segue fail-closed.
 */
export const batemResponsaveis = (
  e: IEmpresaResumo,
  f: FiltrosResumo,
  responsaveis: IResponsaveisPorEmpresa | undefined,
): boolean => {
  if (!f.responsavel && !f.area) return true;
  const daLinha = e.areas ?? {};
  const doMapa = responsaveis?.[e.codi_emp] ?? {};
  if (f.area && !(f.area in daLinha) && !(f.area in doMapa)) return false;
  if (
    f.responsavel &&
    !Object.values(daLinha).includes(f.responsavel) &&
    !Object.values(doMapa).includes(f.responsavel)
  ) {
    return false;
  }
  return true;
};

/**
 * Opções do select "Todos os responsáveis": união fonte-primária (areas por
 * linha, do cadastro) + fallback (mapa das execuções ativas) — espelho exato do
 * universo que `batemResponsaveis` casa, senão haveria opção sem linha (ou
 * linha inalcançável sem opção).
 */
export const opcoesDeResponsaveis = (
  empresas: IEmpresaResumo[],
  responsaveis: IResponsaveisPorEmpresa | undefined,
): string[] => {
  const nomes = new Set<string>();
  empresas.forEach(e => Object.values(e.areas ?? {}).forEach(n => nomes.add(n)));
  Object.values(responsaveis ?? {}).forEach(porArea =>
    Object.values(porArea).forEach(n => nomes.add(n)),
  );
  return Array.from(nomes).sort((a, b) => a.localeCompare(b, 'pt-BR'));
};

/** Opções do select "Todas as áreas" — mesma união das duas fontes. */
export const opcoesDeAreas = (
  empresas: IEmpresaResumo[],
  responsaveis: IResponsaveisPorEmpresa | undefined,
): string[] => {
  const areas = new Set<string>();
  empresas.forEach(e => Object.keys(e.areas ?? {}).forEach(a => areas.add(a)));
  Object.values(responsaveis ?? {}).forEach(porArea =>
    Object.keys(porArea).forEach(a => areas.add(a)),
  );
  return Array.from(areas).sort((a, b) => a.localeCompare(b, 'pt-BR'));
};

/** Sufixo do valor selecionado que sumiu do recorte atual — texto unico. */
export const SUFIXO_FORA_DO_RECORTE = ' (sem empresas neste recorte)';

/**
 * Preserva o valor SELECIONADO como `<option>` mesmo quando ele nao existe mais no
 * conjunto que alimenta o select (achado MEDIO da revisao 09/09).
 *
 * O filtro de SITUACAO CADASTRAL e' o primeiro controle da aba que ENCOLHE o
 * universo de opcoes: com regime="Simples Nacional" escolhido, trocar para
 * "Inativas" (onde nenhuma empresa e' Simples Nacional) fazia a option sumir — o
 * `<select>` controlado caia no placeholder "Todos os regimes" e MENTIA, porque o
 * filtro continuava aplicado e zerava a tabela.
 *
 * Limpar o filtro em silencio nao serve: `filtros.regime`/`filtros.empresa` sao
 * estado COMPARTILHADO com a aba Processos, e a limpeza teria efeito colateral
 * entre abas. Entao o valor fica VISIVEL e honesto, com sufixo dizendo que nao ha'
 * empresa nesse recorte. Valor vazio (placeholder) ou ja' presente = lista intacta.
 */
export const opcoesComValorSelecionado = (
  opcoes: Array<{ value: string; label: string }>,
  selecionado: string,
): Array<{ value: string; label: string }> => {
  if (!selecionado || opcoes.some(o => o.value === selecionado)) return opcoes;
  return [...opcoes, { value: selecionado, label: `${selecionado}${SUFIXO_FORA_DO_RECORTE}` }];
};

/**
 * Todos os filtros em E logico, na mesma cadeia do prototipo. O filtro de BOT e' o
 * unico que EXIGE log: empresa sem rota nunca aparece com ele ativo (as demais
 * dimensoes nao penalizam quem ainda nao tem dado).
 */
export const filtrarEmpresasResumo = <T extends IEmpresaResumo>(
  empresas: T[],
  f: FiltrosResumo,
  responsaveis?: IResponsaveisPorEmpresa,
): T[] => {
  const busca = f.busca.trim().toLowerCase();
  return empresas.filter(e => {
    // SITUACAO CADASTRAL (pedido 09/09): "ativas" (default) esconde inativa/fora
    // da vigencia como sempre, "inativas" mostra SO' essas, "todas" nao recorta.
    // Exclusao de EXIBICAO: o processamento nao passa por aqui.
    if (!casaSituacaoCadastral(e, f.situacaoCadastral)) return false;
    if (f.situacao && e.situacao !== f.situacao) return false;
    if (f.regime && e.empresa?.regime !== f.regime) return false;
    if (f.empresa && e.codi_emp !== f.empresa) return false;
    if (f.grupoEmpresa && e.empresa?.grupo_de_empresas !== f.grupoEmpresa) return false;
    if (f.bot && !e.bots.some(b => b.bot === f.bot)) return false;
    // STATUS (pedido 04/09): ALGUM bot da rota num status selecionado — como o
    // filtro de bot, exige log (empresa sem rota nunca aparece com ele ativo).
    if (f.status.length && !e.bots.some(b => f.status.includes(b.status))) return false;
    if (!batemResponsaveis(e, f, responsaveis)) return false;
    // Busca SÓ no campo do seletor de tipo de pesquisa — "razao" (nome exibido) ou
    // "id" (codi_emp) — pedido 08/09, no MESMO pipeline em E lógico dos demais.
    if (busca && !casaBuscaPorModo(nomeEmpresa(e), e.codi_emp, busca, f.buscaModo)) return false;
    return true;
  });
};

// ── Contagens e headlines ────────────────────────────────────────────────────

/**
 * Reduz uma lista de empresas-com-status ao objeto de contagens canonico.
 *
 * ALERTA ESCONDIDO PELA DEDUP (correcao 15/09): `empresasDoGrupoComStatus` dedup
 * por empresa com `ORDEM_STATUS`, onde `done` (0) vence `warn` (2) — contrato
 * §3.3, e mexer nessa precedencia mudaria a semantica do no'. Consequencia: a
 * empresa que concluiu no bot-b e ALERTOU no bot-a sobrevive como `st: 'done'`,
 * e contar so' por `st` zerava o `warn` do grupo — o `⚠ N` nunca era impresso na
 * meta, embora o popup (que le' `ocorrenciaEmOutroBot`/`motivoEmOutroBot`, #723)
 * mostrasse o alerta. No' e popup discordavam: exatamente a regra de ouro furada.
 *
 * A correcao RECLASSIFICA, nunca soma: a empresa conta 1 vez so', saindo do
 * `done` e entrando no `warn`. Assim `done + warn` nao muda (a barra
 * `passedBarra = done + warn` fica identica — warn nao trava a rota, §3.2) e o
 * total segue sendo `lista.length`, 1 linha por empresa.
 *
 * `err` EM OUTRO BOT AGORA TAMBEM RECLASSIFICA (22/09, pedido do dono):
 *
 *   "precisamos garantir que estamos alocando Sucesso em uma empresa apenas se
 *    ela passar por todas as etapas, em todas as visualizacoes... se por acaso a
 *    empresa X teve falha em qualquer nivel ou em qualquer etapa, entao ela nao
 *    pode alocar como sucesso; o contrario e' valido"
 *
 * Ate aqui `err` era deixado de fora DE PROPOSITO, porque reclassificar muda
 * `tone`, `circleText`, `tagDoNo` e `barErrPct` do no' — de "ok" para "falha". O
 * efeito e' esse mesmo, e agora e' o desejado: uma empresa que falhou em QUALQUER
 * bot do grupo nao pode aparecer dentro do `✓`.
 *
 * MEDIDO em producao antes de mudar (30 dias): 524 empresas saem do sucesso, 13,6%
 * do total. "Transmissao dos Impostos LP" sozinho perde 250 de 534 (46,8%) — sao
 * empresas que hoje contam como concluidas tendo falhado em algum bot do grupo.
 * Grupos de bot unico nao mudam nada (sem segundo bot, nada a mascarar).
 *
 * `warn` SEGUE sem derrubar o sucesso: o alerta sinaliza algo a olhar, mas a
 * empresa FOI processada (§3.2 "warn nao trava a rota"). Ele continua
 * reclassificando `done`->`warn` como antes, o que mantem `done + warn` estavel
 * na barra; o `err` e' que agora sai dos dois.
 *
 * A contagem segue RECLASSIFICANDO, nunca somando: 1 linha por empresa, total =
 * `lista.length`.
 */
/**
 * O STATUS EFETIVO da empresa: o que a dedup elegeu, CORRIGIDO pela ocorrencia
 * escondida em outro bot do grupo.
 *
 * A dedup faz `done` vencer `err` (§3.3), entao a empresa que concluiu no bot-b e
 * falhou no bot-a chega aqui como `done` com `ocorrenciaEmOutroBot: 'err'`. O
 * pedido do dono em 22/09 e' explicito: "se a empresa teve falha em qualquer
 * nivel ou em qualquer etapa, entao ela nao pode alocar como sucesso".
 *
 * Extraida em 24/09 para ser a REGUA UNICA. Ate' aqui a regra vivia so' dentro de
 * `contagensDaLista` (o numero do no'), e o popup contava e filtrava pelo `st`
 * cru: a mesma empresa entrava em "falhas" no no' e em "concluidas" na pilula —
 * era o "6 no cabecalho, 5 na lista".
 *
 * `err` tem precedencia sobre `warn`: falhou em um bot e alertou em outro, manda
 * a falha.
 *
 * REAVALIADO em 24/09, depois de corrigir a falha-que-nao-prescrevia e o
 * `skip`-como-concluida: a regra tira 100 empresas do sucesso (2%), contra 524
 * (13,6%) antes. 424 dos casos de entao eram FALSO POSITIVO daqueles dois bugs —
 * nao era a regra que estava larga, era o dado que estava sujo. As 100 que
 * sobraram tem falha real e acionavel (EFD-REINF invalidada pela Receita,
 * `arquivo_com_erros`, `erro_procuracao`, certificado nao cadastrado).
 */
export const statusEfetivo = (item: IEmpresaComStatus): StatusEsteira =>
  item.st === 'done' && item.ocorrenciaEmOutroBot ? item.ocorrenciaEmOutroBot : item.st;

export const contagensDaLista = (lista: IEmpresaComStatus[]): IContagens => {
  const c: IContagens = { done: 0, skip: 0, err: 0, warn: 0, run: 0, wait: 0 };
  lista.forEach(item => {
    const st = statusEfetivo(item);
    if (st in c) c[st] += 1;
  });
  return c;
};

/**
 * Headline do Resumo: "N empresas no acompanhamento — X concluídas, Y travadas."
 * Empresa sem log entra no total, mas em NENHUMA das duas contagens (prototipo §3.9).
 */
export const headlineResumo = (empresas: IEmpresaResumo[]): string => {
  const concluidas = empresas.filter(e => e.situacao === 'Concluída').length;
  const travadas = empresas.filter(e => e.situacao === 'Em falha').length;
  return `${plural(empresas.length, 'empresa', 'empresas')} no acompanhamento — ${concluidas} concluída${
    concluidas === 1 ? '' : 's'
  }, ${travadas} travada${travadas === 1 ? '' : 's'}.`;
};

/**
 * Headline de Processos — fórmula do protótipo (linhas 2717-2733): conta EMPRESAS
 * com posição ativa e o gargalo é o bot com mais empresas paradas, tudo derivado
 * da coluna `posicao` do /resumo filtrado. O trecho "X itens nas filas" do
 * protótipo somava `step.itens` (itens processados), que o backend não fornece —
 * omitido em vez de reaproveitar o rótulo com outro significado.
 *
 * A UNIDADE é a EMPRESA, não a etapa (correção 09/09 — achado 3 da auditoria de
 * incongruências): o rótulo do protótipo dizia "N etapas ativas" enquanto o número
 * era `empresas.filter(e => e.posicao).length`, ou seja, empresas paradas em alguma
 * etapa. Duas empresas na MESMA etapa contam 2, e a mesma empresa nunca conta duas
 * vezes — "N empresas em etapa ativa" é o que o número de fato mede. Trocar a
 * frase (e não a conta) é deliberado: o número sempre bateu com o board.
 *
 * `exibirNomeBot` traduz o nome REAL do gargalo para o apelido de exibição (o
 * agrupamento continua pelo nome real — apelido nunca vira chave).
 */
export const headlineProcessos = (
  empresas: IEmpresaResumo[],
  // eslint-disable-next-line no-unused-vars
  exibirNomeBot: (bot: string) => string = bot => bot,
): string => {
  const emEtapaAtiva = empresas.filter(e => e.posicao).length;
  if (!emEtapaAtiva) {
    return 'Nenhuma empresa em etapa ativa no momento — tudo concluído ou fora do período/filtro selecionado.';
  }
  const paradasPorBot = new Map<string, number>();
  empresas.forEach(e => {
    if (e.posicao) paradasPorBot.set(e.posicao.bot, (paradasPorBot.get(e.posicao.bot) ?? 0) + 1);
  });
  let gargalo = '';
  let maior = 0;
  paradasPorBot.forEach((qtd, bot) => {
    if (qtd > maior) {
      maior = qtd;
      gargalo = bot;
    }
  });
  return `${plural(emEtapaAtiva, 'empresa em etapa ativa', 'empresas em etapa ativa')}.${
    gargalo ? ` ${exibirNomeBot(gargalo)} concentra o maior acúmulo.` : ''
  }`;
};

/** Headline de Dependencias: "N bots no catálogo — M não dependem de nenhum outro..." */
export const headlineDependencias = (vinculos: IVinculoBot[]): string => {
  const raizes = vinculos.filter(v => !v.dep_bot).length;
  return `${plural(vinculos.length, 'bot', 'bots')} no catálogo — ${raizes} não depende${
    raizes === 1 ? '' : 'm'
  } de nenhum outro para começar.`;
};
